import { useState } from 'react';
import useModal from './modalHook';
import QuoteIframe from '../components/dashboard/quoteWidget/QuoteWidget';
import PremiumFinancingPreapproval from '../views/shared/premiumFinancing/PremiumFinancingPreapprovalModal';
import { useQueryClient } from '@tanstack/react-query';
import EditReviewStage from '../views/shared/premiumFinancing/EditReviewStage';
import ActionConfirmation from '../components/elements/ActionConfirmation';
import { applyLoanToPremiumFinancing, approvePremiumFinancingApplication, generatePremiumFinancingDocuments, generatePremiumFinancingPayments, getBrokerPremiumFinancingMetrics, getPremiumFinancingDocuments, getPremiumFinancingPayments, getSinglePremiumFinancingByApplicationNumber, InitializeHubtelDirectCharge, resetSinglePayment, runPaymentAnalysisOnPremiumFinancing, sendPayNowNotification, sharePremiumFinancingAgreementDocumentLink } from '../api/requests';
import { PremiumFinancing } from '../models/financing/PremiumFinancing';
import PaymentLinkModal from '../views/shared/policies/auto/paymentLinkModal';
import PaymentEditModal from '../views/shared/payments/PaymentEditModal';
import { getPremiumFinancingMetrics } from '../api/requests';
import useUserAccount from './userAccountHook';
import { useParams } from 'react-router-dom';
import { IPayment } from '../models/payment/IPayment';
import { ICustomerPayments } from '../resources/interfaces';
import ApprovePaymentModal from '../views/shared/payments/PaymentApproval';
import { showToast } from '../utils/miscFunctions';

const usePremiumFinancing = () => {
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const FinancingModal = ModalComponent;
  const queryClient = useQueryClient();
  const { user } = useUserAccount()
  const params = useParams();
  const applicationId = params?.id;
  const [financingApplication, setFinancingApllication] = useState<PremiumFinancing>();
  const [paymentAnalysis, setPaymentAnalysis] = useState<any>([]);
  const [documents, setdocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingPayment, setLoadingPayment] = useState(true);
  const [payments, setPayments] = useState<any>([]);

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['displayFinancingApplication'] });
  };

  const fetchPremiumFinancingMetrics = async (companyId?: string, startDate?: string, endDate?: string) => {
    // if (!companyId) return;
    try {
      if (user.isBroker()) {
        const response: any = await getBrokerPremiumFinancingMetrics(Number(companyId), startDate, endDate);

        return {
          totalApplications: response?.totalApplications || 0,
          totalFinancingAmount: response?.totalPremium || 0,
          approvedFinancingAmount: response?.premiumByStatus.APPROVED || 0,
          approvedApplications: response?.applicationsByStatus.APPROVED || 0,
          inApprovalFinancingAmount: response?.premiumByStatus?.IN_APPROVAL || 0,
          inApprovalApplications: response?.applicationsByStatus.IN_APPROVAL || 0,
          pendingApplications: response?.applicationsByStatus.PENDING || 0,
          monthlyTrend: response?.monthlyTrend || [],
          statusDistribution: response?.statusDistribution || [],
          approvalRate: response?.approvalRate || 0,
          inReviewApplications: response?.applicationsByStatus.IN_REVIEW || 0,
          inReviewFinancingAmount: response?.premiumByStatus.IN_REVIEW || 0,
        }
      } else {
        const response: any = await getPremiumFinancingMetrics(companyId ? Number(companyId) : undefined, startDate, endDate);
        return {
          totalApplications: response?.totalApplications || 0,
          totalFinancingAmount: response?.totalFinancingAmount || 0,
          approvedFinancingAmount: response?.approvedFinancingAmount || 0,
          pendingApplications: response?.pendingApplications || 0,
          rejectedApplications: response?.rejectedApplications || 0,
          approvedApplications: response?.approvedApplications || 0,
          monthlyTrend: response?.monthlyTrend || [],
          statusDistribution: response?.statusDistribution || [],
          approvalRate: response?.approvalRate || 0,
        }
      }

    } catch (error) {
      console.error('Error fetching premium financing metrics:', error);
      return null;
    }
  };

  const getPremiumFinancingQuote = () => {
    updateModalStates({
      show: true,
      size: '3xl',
      children: (
        <QuoteIframe
          onProceed={() => {
            closeModal();
          }}
          onClose={closeModal}
          isPremiumFinancing={true}
        />
      ),
      static: true,
      isHeadRounded: false
    });
  };

  const premiumFinancingPreapproval = (financingApplication: PremiumFinancing) => {
    financingApplication &&
      updateModalStates({
        show: true,
        size: 'sm',
        children: (
          <PremiumFinancingPreapproval
            financingApplication={financingApplication}
            onProceed={() => {
              closeModal();
              invalidateQueries();
            }}
            onClose={closeModal}
          />
        ),
        showCloseButton: false,
        classes: '!p-0 r-[5px]'
      });
  };

  const approveApplication = () => {
    updateModalStates({
      show: true,
      size: 'sm',
      showCloseButton: false,
      children: (
        <ActionConfirmation
          action="approve"
          type="success"
          resourceType={''}
          resourceName={''}
          onClose={closeModal}
          onConfirm={() => {
            if (!applicationId) return
            approvePremiumFinancingApplication(applicationId)
              .then((response: any) => {
                showToast.success(response?.message || 'Application submitted successfully.');
                closeModal();
                invalidateQueries();
              })
              .catch((error: any) => {
                showToast.error(error?.message || 'Application submitted error Encountered.');
              });
          }}
        />
      )
    });
  };

  const handleResetPayment = async (payment: IPayment, onProceed: () => void) => {
    updateModalStates({
      show: true,
      showCloseButton: false,
      size: 'md',
      children: (
        <ActionConfirmation
          action="Reset"
          modalName="Payment"
          type='warning'
          resourceName={'payment'}
          onClose={closeModal}
          onConfirm={async () => {
            try {
              const response = await resetSinglePayment(Number(payment.id))

              if (response) {
                closeModal();
                showToast.success(response?.message ?? 'Payment reset successfully', );
                onProceed();
              }
            } catch (error: any) {
              closeModal();
              showToast.error(error.description ?? 'Failed to reset payment', );
            }
          }}
        />
      )
    });
  };

  const editReviewStage = (financingApplication: PremiumFinancing) => {
    financingApplication &&
      updateModalStates({
        show: true,
        size: 'sm',
        children: (
          <EditReviewStage
            financingApplication={financingApplication}
            onProceed={() => {
              closeModal();
              invalidateQueries();
            }}
            onClose={closeModal}
          />
        ),
        showCloseButton: false,
        classes: '!p-0 r-[5px]'
      });
  };

  const showPaymentLinkModal = (paymentLink: string, paymentId: number, phone?: string) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <PaymentLinkModal
          onProceed={() => {
            closeModal();
            invalidateQueries()
          }}
          onClose={closeModal}
          link={paymentLink}
          paymentId={paymentId}
          phone={phone}
        />
      ),

      title: 'Payment Link',
      classes: '!p-0 r-[5px]'
    });
  };

  const showEditPaymentModal = (payment: any) => {
    updateModalStates({
      show: true,
      size: '4xl',
      children: (
        <PaymentEditModal
          onProceed={() => {
            closeModal();
            invalidateQueries();
          }}
          payment={payment}
          onClose={closeModal}
        />
      ),
      showTitleSection: false,
      showCloseButton: false,
    });
  };

  const handleApplyLoan = (id: string) => {
    try {
      const response: any = applyLoanToPremiumFinancing(id)

      if (response) {
        showToast.success(response?.message || "Loan applied successfully")
        closeModal();
        invalidateQueries();
      }
    }
    catch (error: any) {
      showToast.error(error?.description || 'Failed to apply loan')
    }
  }

  const getDocuments = async () => {
    financingApplication &&
      (await getPremiumFinancingDocuments(financingApplication?.applicationNumber)
        .then((response: any) => {
          setdocuments(response?.data);
        })
        .catch((ror: Error) => {
          console.log('error', ror);
        }));
  };

  const generateDocuments = async () => {

    financingApplication && await generatePremiumFinancingDocuments(financingApplication?.applicationNumber)
      .then((response: any) => {
        setdocuments(response?.data);
        invalidateQueries();
        showToast.success(response?.message && 'Documents generated successfully.');
      })
      .catch((ror: Error) => {
        console.log('error', ror);
        showToast.error(ror && 'Failed to generate Documents. An error occured');
      });
  };

  const runPaymentAnalysis = () => {
    runPaymentAnalysisOnPremiumFinancing(Number(financingApplication?.applicationNumber))
      .then((response) => {
        setPaymentAnalysis(response);
      })
      .catch((error: Error) => {
        console.log('error', error);
      })
      .finally(() => {
        invalidateQueries();
      });
  };

  const displayFinancingApplication = () => {
    applicationId &&
      getSinglePremiumFinancingByApplicationNumber(applicationId)
        .then((response: any) => {

          setFinancingApllication(new PremiumFinancing(response?.data));

          setPaymentAnalysis(response?.data?.paymentAnalysis);
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          setLoading(false);
        });
  };

  const allApplicationPayments = async () => {
    try {
      financingApplication &&
        financingApplication?.applicationNumber &&
        (await getPremiumFinancingPayments(String(financingApplication?.applicationNumber))
          .then((response: any) => {
            setPayments(response?.data);
          })
          .catch((error: any) => {
            console.log(error);
          })
          .finally(() => {
            setLoadingPayment(false);
          }));
    } catch (error) {
      console.log(error);
    }
  };

  const sendPaymentNotification = (payment: IPayment) => {
    // const payment: ICustomerPayments | undefined = props.policy?.getFirstUnpaidPayment();
    if (payment) {
      sendPayNowNotification(payment?.id, payment?.productType?.key)
        .then((response: any) => {
          showToast.success(response?.message ?? 'Payment link notification sent successfully');
        })
        .catch((error: any) => {
          showToast.error(error?.description ?? 'Failed to send payment link notification.');
        });
    } else {
      showToast.error('Failed to send payment link notification. Try again later.');
    }
  };
  
  const showPaymentApprovalModal = (payment: IPayment, onProceed: () => void) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <ApprovePaymentModal
          onSubmitted={() => {
            onProceed();
            closeModal();
          }}
          onCancel={closeModal}
          data={payment}
          title={'Approve Payment Edit'}
        />
      ),

      classes: '!p-0 r-[5px]'
    });
  };

  const InitiateHubtelDirectCharge = (payment: ICustomerPayments) => {
    // const payment: ICustomerPayments | undefined = props.policy?.getFirstUnpaidPayment();
    if (payment) {
      InitializeHubtelDirectCharge(payment?.id)
        .then((response: any) => {
          showToast.success(response?.message ?? 'Hubtel direct charge initiated successfully');
        })
        .catch((error: any) => {
          showToast.error(error?.description ?? 'Failed to initiate Hubtel direct charge.');
        });
    } else {
      showToast.error('Failed to initiate Hubtel direct charge. Try again later.');
    }
  };

  const sendAgreementFormToCustomer = () => {
    updateModalStates({
      show: true,
      children: (
        <ActionConfirmation
          action="share the document agreement"
          type='success'
          resourceType={''}
          resourceName={''}
          onClose={closeModal}
          onConfirm={async () => {
            try {
              if (financingApplication) {
                const response = await sharePremiumFinancingAgreementDocumentLink(String(financingApplication?.applicationNumber));
                if (response) {
                  showToast.success(response?.message ?? 'Document shared successfully.');
                  closeModal()
                }
              }
            } catch (error: any) {
              showToast.error(error?.description ?? 'Failed to share financingApplication document.');
            } finally {

            }
          }}
        />
      ),
      size: 'sm',
      title: 'Share Agreement Document',
      showTitleSection: true,
    });
  }

  const handleGeneratePremiumFinancingPayments = async (paymentDate: string, invalidateQueriesPayments: () => void) => {
    try {
      const response: any = await generatePremiumFinancingPayments(String(financingApplication?.applicationNumber), paymentDate)
      if (response) {
        showToast.success(response?.message || 'Payment Generated successfully.');
        invalidateQueriesPayments();
      }
    } catch (error: any) {
      showToast.error(error?.description || 'Generate payment has failed.');
      console.log(error);
    }
  }


  return {
    getPremiumFinancingQuote,
    FinancingModal,
    premiumFinancingPreapproval,
    approveApplication,
    editReviewStage,
    showPaymentLinkModal,
    InitiateHubtelDirectCharge,
    showEditPaymentModal,
    fetchPremiumFinancingMetrics,
    handleApplyLoan,
    showPaymentApprovalModal,
    generateDocuments,
    documents,
    getDocuments,
    financingApplication,
    paymentAnalysis,
    payments,
    displayFinancingApplication,
    handleResetPayment,
    sendPaymentNotification,
    allApplicationPayments,
    runPaymentAnalysis,
    loading,
    loadingPayment,
    sendAgreementFormToCustomer,
    handleGeneratePremiumFinancingPayments
  };
};

export default usePremiumFinancing;
